// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { credits as creditsText } from 'Utils/numberFormat';

// What a catalog checkout says pays for the order (EXP-20261020-70, -75; AUT-6736; one rate per account, 2026-10-29). The
// rule is the server's (saveCatalogOrder, catalogPricing.splitCatalogCharge): the account's credit balance pays first, at
// the cart's $/credit, the same balance an AEC order draws; the rest follows the account kind, as an AEC order's does —
// invoiced against a pre-commit plan's commitment, with nothing on the card; on the default card for every other account
// (the total without the license fee, which is invoiced, plus the sales tax on that charge).
//
// Claude's plain wording (Joel, 2026-10-20); Janan may revise it here. The order form's price block, Billing and
// Payment, the estimate and invoice PDF and the e2e tests (e2e/tests/test_catalog_checkout_wording.py reads this
// object, so each entry stays one plain string literal: no concatenation, no ${}) all print these words. {name} is
// filled in.
export const CATALOG_BILLING_COPY = {
  invoiced: 'Invoiced against your plan. Nothing is charged to your card.',
  cardCharge: '{amount} will be charged to {card} when you submit this order.',
  cardEnding: 'your card ending {last4}',
  cardUnnamed: 'your card',
  // an order held for an imagery request (Submit saves it; it is placed, and charged, once the imagery is in) and an
  // estimate PDF
  cardAtPlacement: 'Charged to {card} when the order is placed.',
  cardCharged: 'Charged to your card.',
  // the added license fee beside a card charge: it is never charged to a card (AUT-6569 D4)
  licenseFeeInvoiced: 'invoiced separately',
  // the caption under the credits row: whose rate prices them (one rate per account) — the list rate at Entry, the
  // account's ladder tier, its negotiated figure, pay-as-you-go, or a tier the order's own size earned
  listRate: 'Catalog list rate',
  tierRate: 'Catalog {tier} tier rate',
  yourRate: 'Your rate',
  paygRate: 'Pay-as-you-go rate',
  earnedRate: 'Catalog {tier} tier rate, earned by this order',
  // the credits the account's prepaid balance pays (AUT-6736), under the Total beside the rest's card or invoice note
  fromBalance: '{credits} credits from your prepaid balance.',
  // the minimum project fee on a balance account (D6, 2026-10-29): dollars on the card or the invoice, never credits
  feeOnCard: 'The minimum project fee is charged to your card in dollars; it does not use your credits.',
  feeInvoiced: 'The minimum project fee is invoiced in dollars; it does not use your credits.',
};

const fill = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (placeholder, name: string) => values[name] ?? placeholder);

export type CatalogBilling = 'card' | 'invoiced';

// What pays the rest after the balance follows the account kind, as the server's split reads it: a pre-commit plan is
// invoiced against its commitment; every other account (pre-pay, pay-as-you-go, a Stripe subscription) pays on its card.
export const catalogBilling = (pricingPlanName?: string | null): CatalogBilling => (pricingPlanName === 'enterprise_pre_commit' ? 'invoiced' : 'card');

// The discount row's label on the order summary and the estimate PDF: the ladder tier the cart priced at, or the
// account's negotiated rate when its figure sits between (or below) the ladder's tiers (the quote's tierName is then
// 'negotiated', not a tier's name).
export const catalogDiscountRowLabel = (tierName?: string | null): string => {
  if (tierName === 'negotiated') return 'Negotiated Rate Discount';
  return tierName ? `${tierName} Tier Discount` : 'Discount';
};

export interface CatalogChargeSplit {
  drawCredits: number;
  restDollar: number;
  restMode: CatalogBilling | null;
}

interface CatalogChargeSplitInputs {
  // the total without the license fee and before sales tax (0 for a cart that needs a quote)
  chargeableDollar: number;
  // the $/credit the cart priced at
  cartRate: number;
  // the quote's balanceCredits: the balance a placement draws on first; null when the account holds none
  balanceCredits?: number | null;
  // the minimum project fee inside chargeableDollar: dollars the balance never pays (D6, 2026-10-29)
  feeDollar?: number;
  // the account's plan name; null for a Stripe or plan-less account
  pricingPlanName?: string | null;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

// The server's rule (catalog_pricing.js splitCatalogCharge) on the same served figures, so the form says what checkout
// does: the balance covers what it can at the cart's rate; the rest is invoiced on a pre-commit plan and goes on the card
// for everyone else.
export const splitCatalogCharge = ({ chargeableDollar, cartRate, balanceCredits, pricingPlanName, feeDollar = 0 }: CatalogChargeSplitInputs): CatalogChargeSplit => {
  const dollarsDue = Number.isFinite(chargeableDollar) && chargeableDollar > 0 ? chargeableDollar : 0;
  // the minimum project fee is dollars, never credits: the balance pays the product only, the fee rides on the rest
  const fee = Number.isFinite(feeDollar) && feeDollar > 0 ? Math.min(feeDollar, dollarsDue) : 0;
  const creditDollars = round2(dollarsDue - fee);
  const balance = typeof balanceCredits === 'number' && balanceCredits > 0 ? balanceCredits : 0;
  const rate = Number.isFinite(cartRate) && cartRate > 0 ? cartRate : null;
  const neededCredits = rate === null ? null : round2(creditDollars / rate);
  const drawCredits = neededCredits === null ? 0 : Math.min(balance, neededCredits);
  const covered = neededCredits !== null && drawCredits >= neededCredits;
  const creditRest = covered ? 0 : Math.max(0, round2(creditDollars - drawCredits * (rate ?? 0)));
  const restDollar = round2(creditRest + fee);
  let restMode: CatalogBilling | null = null;
  if (restDollar > 0) restMode = catalogBilling(pricingPlanName);
  return { drawCredits, restDollar, restMode };
};

// The sentence beside the fee on a balance account (D6): what pays it, and that the credits are untouched
export const catalogFeeNote = (restMode: CatalogBilling | null): string =>
  (restMode === 'invoiced' ? CATALOG_BILLING_COPY.feeInvoiced : CATALOG_BILLING_COPY.feeOnCard);

// The note for the credits the balance pays
export const catalogBalanceNote = (drawCredits: number): string => fill(CATALOG_BILLING_COPY.fromBalance, { credits: creditsText(drawCredits) });

const cardText = (last4?: string | null): string => (last4 ? fill(CATALOG_BILLING_COPY.cardEnding, { last4 }) : CATALOG_BILLING_COPY.cardUnnamed);

// The note under a card-charged Total: the amount and the default card it goes on.
export const catalogCardChargeNote = (amountText: string, last4?: string | null): string =>
  fill(CATALOG_BILLING_COPY.cardCharge, { amount: amountText, card: cardText(last4) });

// The same order held for its imagery request: SUBMIT saves it, and the card is charged when it is placed, at a total
// confirmed then.
export const catalogHeldCardNote = (last4?: string | null): string => fill(CATALOG_BILLING_COPY.cardAtPlacement, { card: cardText(last4) });

interface CatalogCreditsCaptionInputs {
  // the tier the cart priced at (the quote's per-product / cart tierName): the account's position, or a better tier earned
  tierName?: string | null;
  // the account's own position (the quote's accountTierName): a ladder tier, 'negotiated' between tiers, 'Standard' at or above list
  accountTierName?: string | null;
  // where the account's rate comes from (the quote's rateSource)
  rateSource?: string | null;
}

const isLadderTier = (name?: string | null): boolean => !!name && name !== 'Standard' && name !== 'negotiated';

// The caption under the catalog credits row: whose rate prices them (one rate per account). A tier the order's own size
// earned above the account's position says so; otherwise the account's own position: its ladder tier, its negotiated
// figure ("Your rate"), pay-as-you-go, or the list rate at Entry.
export const catalogCreditsCaption = ({ tierName, accountTierName, rateSource }: CatalogCreditsCaptionInputs): string => {
  if (isLadderTier(tierName) && tierName !== accountTierName) {
    return fill(CATALOG_BILLING_COPY.earnedRate, { tier: tierName as string });
  }
  if (accountTierName === 'negotiated') return CATALOG_BILLING_COPY.yourRate;
  if (rateSource === 'payg' || rateSource === 'payg_override') return CATALOG_BILLING_COPY.paygRate;
  if (isLadderTier(accountTierName)) return fill(CATALOG_BILLING_COPY.tierRate, { tier: accountTierName as string });
  return CATALOG_BILLING_COPY.listRate;
};

interface CatalogPdfBillingInputs {
  type: string;
  // the account's plan name, which decides card or invoice for the rest; absent when the data does not say
  pricingPlanName?: string | null;
  totalAdditionalCharged?: number;
  requiresManualQuote?: boolean;
  // the credits the balance paid (AUT-6736): the form's split on an estimate, the order's record on an invoice
  balanceCreditsDrawn?: number | null;
  // whether the rest after the draw is invoiced; the form knows, the server's PDF data does not say
  restInvoiced?: boolean;
  // the minimum project fee on the order, and whether the account holds a credit balance: together they earn the
  // sentence that the fee is dollars, not credits (D6); an invoice earns it when credits were drawn
  feeDollar?: number;
  holdsBalance?: boolean;
}

// The catalog PDF's note. The credits the balance paid come first (AUT-6736). A card amount on the record was (or will
// be) charged to the card. With nothing on the card, the rest is invoiced when the form says so, or, with no draw, when
// the account is on a pre-commit plan (the form's estimate and the server's PDF data both name the plan). Data that
// names no plan claims nothing: an invoice that put nothing on the card of a card account (a $0 override) says nothing
// rather than claim an invoice.
export const catalogPdfBillingNote = ({
  type, pricingPlanName, totalAdditionalCharged, requiresManualQuote, balanceCreditsDrawn, restInvoiced, feeDollar, holdsBalance,
}: CatalogPdfBillingInputs): string | null => {
  if (requiresManualQuote || typeof totalAdditionalCharged !== 'number') return null;
  const invoice = type === 'Invoice';
  const drawn = typeof balanceCreditsDrawn === 'number' && balanceCreditsDrawn > 0 ? catalogBalanceNote(balanceCreditsDrawn) : null;
  const billing: CatalogBilling | null = pricingPlanName === undefined ? null : catalogBilling(pricingPlanName);
  let rest: string | null = null;
  const invoiced = restInvoiced === true || (restInvoiced === undefined && !drawn && billing === 'invoiced');
  if (totalAdditionalCharged > 0) {
    rest = invoice ? CATALOG_BILLING_COPY.cardCharged : catalogHeldCardNote(null);
  } else if (invoiced) {
    rest = CATALOG_BILLING_COPY.invoiced;
  } else if (!drawn && !invoice && billing === 'card') {
    rest = catalogHeldCardNote(null);
  }
  const feeApplies = typeof feeDollar === 'number' && feeDollar > 0 && (holdsBalance === true || !!drawn);
  const fee = feeApplies ? catalogFeeNote(invoiced ? 'invoiced' : 'card') : null;
  const parts = [drawn, rest, fee].filter((part): part is string => !!part);
  return parts.length ? parts.join(' ') : null;
};
